import { useState } from "react";
import { Link } from "react-router-dom";
import { LogoFull } from "./Logo";
import { ChevronDown, ChevronUp, Search, FileText, CreditCard, Settings, Users, BarChart3, RefreshCw, Mail, Shield, BookOpen, Receipt } from "lucide-react";

const faqs = [
  {
    category: "Getting Started",
    icon: FileText,
    items: [
      { q: "How do I create my first invoice?", a: "Click 'New Invoice' from your dashboard. Add a client (or create one), fill in line items with descriptions and amounts, set your currency, and hit 'Create'. The whole process takes under 30 seconds." },
      { q: "How do I add my business logo?", a: "Go to Settings > Business Information. You can upload a PNG, JPG, or SVG logo (max 2MB). It will appear on all your invoices and PDFs automatically." },
      { q: "Can I use VeroBill on my phone?", a: "Yes. VeroBill is a Progressive Web App (PWA). On mobile, open the site in your browser, tap 'Add to Home Screen', and it works like a native app — offline-capable and fast." },
    ]
  },
  {
    category: "Invoicing",
    icon: FileText,
    items: [
      { q: "How do I send an invoice to a client?", a: "Open any invoice and click 'Send'. You can share the public link directly, or use the built-in email feature to send it with a personal message. Clients can view and pay without creating an account." },
      { q: "Can I duplicate an invoice?", a: "Yes. Open any invoice and click the 'Duplicate' button. This creates an exact copy with a new invoice number — perfect for recurring work." },
      { q: "What currencies are supported?", a: "VeroBill supports 20 currencies including USD, EUR, GBP, INR, AUD, CAD, SGD, AED, JPY, and more. Set your default in the invoice builder." },
      { q: "How do I track invoice status?", a: "Invoices have 4 statuses: Draft, Sent, Paid, and Overdue. Use the dashboard filters to see invoices by status. Mark invoices as paid manually or let payment integrations do it automatically." },
      { q: "What are tax templates?", a: "VeroBill has built-in tax configurations for 12 countries (US, UK, EU, India, Australia, etc.). Set your default country in Settings, and new invoices auto-populate the correct tax lines and compliance notes." },
    ]
  },
  {
    category: "Estimates & Quotes",
    icon: Users,
    items: [
      { q: "What's the difference between an estimate and an invoice?", a: "An estimate is a quote you send before doing the work. Once your client accepts it, you can convert it to a draft invoice with one click. Estimates are unlimited on all plans." },
      { q: "Can clients accept or decline estimates?", a: "Yes. When you send an estimate, clients get a public link where they can review the details and click 'Accept' or 'Decline'. You'll see the status update in your dashboard." },
    ]
  },
  {
    category: "Recurring Invoices",
    icon: RefreshCw,
    items: [
      { q: "How do I set up recurring invoices?", a: "Create a regular invoice first, then go to Recurring Invoices and click 'New Recurring'. Select the invoice as a template, choose your interval (weekly, monthly, quarterly, etc.), and VeroBill will auto-generate invoices on schedule." },
      { q: "Can I pause a recurring invoice?", a: "Yes. Toggle the pause/resume button on any recurring invoice. Paused invoices won't generate new ones until you resume them." },
    ]
  },
  {
    category: "Payments",
    icon: CreditCard,
    items: [
      { q: "How do clients pay my invoices?", a: "When you connect a payment provider (Stripe, PayPal, Razorpay, or Wise), a 'Pay Now' button appears on your public invoice link. Clients click it to pay — the money goes directly to your account." },
      { q: "Does VeroBill take a cut of payments?", a: "No. VeroBill never touches your money. You connect your own Stripe, PayPal, Razorpay, or Wise accounts. 100% of the payment goes to you. Standard payment processor fees still apply." },
      { q: "How do I connect Stripe?", a: "Go to Settings > Payment Integration > Stripe. Add your Publishable Key and Secret Key from dashboard.stripe.com/apikeys. A 'Pay with Card' button will then appear on all your invoices." },
    ]
  },
  {
    category: "Email",
    icon: Mail,
    items: [
      { q: "How does email sending work?", a: "VeroBill sends invoice emails on your behalf using our verified domain. Your business name appears as the sender. When clients hit 'Reply', it goes to your reply-to email address (set in Settings)." },
      { q: "Can I send from my own domain?", a: "Yes, if you have a Resend account. Add your Resend API key in Settings > Email Integration. This lets you send from your own verified domain for a more professional appearance." },
    ]
  },
  {
    category: "Security",
    icon: Shield,
    items: [
      { q: "Are my API keys safe?", a: "Yes. All sensitive data (Stripe keys, Razorpay keys, Resend API keys) is encrypted at rest using AES-256 Fernet encryption. Keys are never stored in plain text and are masked in the UI." },
      { q: "Is there a session timeout?", a: "Access tokens expire after 60 minutes. VeroBill automatically refreshes your session using a secure refresh token (valid for 7 days). You stay logged in as long as you're active." },
    ]
  },
  {
    category: "Branding & Templates",
    icon: Settings,
    items: [
      { q: "Can I customize my invoice PDF?", a: "Yes. Go to Settings > Branding & PDF Templates. Pick your brand color using the color wheel, then choose from 3 templates: Classic (logo + clean layout), Modern (bold color banner), or Minimal (text-only, thin lines)." },
      { q: "Can I remove the VeroBill branding?", a: "Yes. Toggle off 'Show Powered by VeroBill' in Settings > Branding. Pro and Lifetime Deal users have this on by default." },
    ]
  },
];

export default function HelpDocs() {
  const [searchTerm, setSearchTerm] = useState("");
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (catIdx, itemIdx) => {
    const key = `${catIdx}-${itemIdx}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const filtered = faqs.map(cat => ({
    ...cat,
    items: cat.items.filter(item =>
      !searchTerm || item.q.toLowerCase().includes(searchTerm.toLowerCase()) || item.a.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(cat => cat.items.length > 0);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <header className="border-b border-[#E5E7EB] bg-white sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" data-testid="help-logo"><LogoFull size={24} /></Link>
          <nav className="flex items-center gap-6">
            <Link to="/blog" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Blog</Link>
            <Link to="/changelog" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Changelog</Link>
            <Link to="/login" className="text-[13px] font-medium text-[#0A0A0A] border border-[#0A0A0A] px-4 py-1.5 hover:bg-[#0A0A0A] hover:text-white transition-colors" data-testid="help-login-btn">Log in</Link>
          </nav>
        </div>
      </header>

      <section className="py-14 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-3 py-1 text-[11px] font-mono uppercase tracking-wider mb-6">
            <BookOpen className="w-3 h-3" />Help Centre
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-[#0A0A0A] tracking-tight leading-[1.1] mb-4">
            How can we help?
          </h1>
          <p className="text-base text-[#6B7280] mb-8">Quick answers to common questions about VeroBill.</p>
          <div className="relative max-w-lg mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search for answers..."
              className="w-full pl-10 pr-4 py-3 border border-[#E5E7EB] text-sm focus:outline-none focus:border-[#0A0A0A]"
              data-testid="help-search"
            />
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-20 space-y-8">
        {filtered.map((cat, catIdx) => {
          const Icon = cat.icon;
          return (
            <div key={cat.category} className="border border-[#E5E7EB] bg-white" data-testid={`help-category-${catIdx}`}>
              <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center gap-2">
                <Icon className="w-4 h-4 text-[#6B7280]" />
                <h2 className="text-base font-bold text-[#0A0A0A]">{cat.category}</h2>
                <span className="text-[11px] text-[#9CA3AF] font-mono ml-auto">{cat.items.length}</span>
              </div>
              <div className="divide-y divide-[#F3F4F6]">
                {cat.items.map((item, itemIdx) => {
                  const isOpen = openItems[`${catIdx}-${itemIdx}`];
                  return (
                    <div key={itemIdx}>
                      <button
                        onClick={() => toggleItem(catIdx, itemIdx)}
                        className="w-full text-left px-6 py-4 flex items-center justify-between gap-4 hover:bg-[#FAFAF9] transition-colors"
                        data-testid={`help-q-${catIdx}-${itemIdx}`}
                      >
                        <span className="text-[14px] font-medium text-[#0A0A0A]">{item.q}</span>
                        {isOpen ? <ChevronUp className="w-4 h-4 text-[#9CA3AF] shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#9CA3AF] shrink-0" />}
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-4">
                          <p className="text-[14px] text-[#6B7280] leading-relaxed">{item.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </section>

      <footer className="border-t border-[#E5E7EB] py-8 px-6 bg-white">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <LogoFull size={20} />
          <div className="flex items-center gap-6">
            <Link to="/" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Home</Link>
            <Link to="/blog" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Blog</Link>
            <Link to="/changelog" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Changelog</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
